import { useRef, useState } from 'react';
import type { QuestionColumns, Theme } from '@floorpoll/shared';
import { themeContrastWarnings } from '../../lib/contrast.js';
import { quizApi } from '../../lib/quizApi.js';
import { useTourAnchor } from '../../lib/tour/anchors.js';

const COLOR_FIELDS: { key: keyof Theme; label: string }[] = [
  { key: 'primaryColor', label: 'Primary' },
  { key: 'accentColor', label: 'Accent' },
  { key: 'backgroundColor', label: 'Background' },
  { key: 'textColor', label: 'Text' },
];

// Labelled in room terms, not grid terms: "screen" already means a poll's
// Screen entity in this product, so the copy says display.
const COLUMN_CHOICES: { value: QuestionColumns; label: string }[] = [
  { value: 1, label: 'One at a time (stacked)' },
  { value: 2, label: 'Two across' },
  { value: 3, label: 'Three across' },
];

interface Props {
  quizId: string;
  theme: Theme;
  disabled: boolean;
  onChange: (theme: Theme) => void;
}

/** Stories #18 and #19 — branding, with a live preview of the public views. */
export function ThemeEditor({ quizId, theme, disabled, onChange }: Props) {
  const [uploading, setUploading] = useState<'logo' | 'background' | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const logoInput = useRef<HTMLInputElement>(null);
  const backgroundInput = useRef<HTMLInputElement>(null);
  const themeAnchor = useTourAnchor('poll-theme');

  async function upload(kind: 'logo' | 'background', file: File | undefined) {
    if (!file) return;
    setUploadError(null);
    setUploading(kind);

    try {
      const url = await quizApi.uploadAsset(quizId, kind, file);
      onChange({ ...theme, [kind === 'logo' ? 'logoUrl' : 'backgroundImageUrl']: url });
    } catch (caught) {
      setUploadError(
        caught instanceof Error
          ? caught.message
          : 'Upload failed. Try a PNG, JPEG or WebP under 5 MB.',
      );
    } finally {
      setUploading(null);
    }
  }

  return (
    // Same card as a screen, minus the cue number: branding is not a stop in
    // the running order, and giving it one would put it in the rail.
    <div className="screen-card branding-card" ref={themeAnchor}>
      <header className="screen-card-head">
        <div className="screen-card-title">
          <h2>Branding</h2>
          <p className="screen-card-note">
            How the big screen and everyone&rsquo;s phone will look
          </p>
        </div>
      </header>

      <div className="color-grid">
        {COLOR_FIELDS.map(({ key, label }) => (
          <div className="field" key={key}>
            <label htmlFor={`theme-${key}`}>{label}</label>
            <input
              id={`theme-${key}`}
              type="color"
              className="color-input"
              disabled={disabled}
              value={(theme[key] as string) ?? '#000000'}
              onChange={(e) => onChange({ ...theme, [key]: e.target.value })}
            />
          </div>
        ))}
      </div>

      <div className="row">
        <div className="field">
          <label htmlFor="theme-logo">Logo</label>
          <input
            id="theme-logo"
            ref={logoInput}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            disabled={disabled || uploading !== null}
            onChange={(e) => void upload('logo', e.target.files?.[0])}
          />
          {theme.logoUrl ? (
            <button
              type="button"
              className="secondary field-clear"
              disabled={disabled}
              onClick={() => {
                onChange({ ...theme, logoUrl: null });
                if (logoInput.current) logoInput.current.value = '';
              }}
            >
              Remove logo
            </button>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="theme-background">Background image</label>
          <input
            id="theme-background"
            ref={backgroundInput}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            disabled={disabled || uploading !== null}
            onChange={(e) => void upload('background', e.target.files?.[0])}
          />
          {theme.backgroundImageUrl ? (
            <button
              type="button"
              className="secondary field-clear"
              disabled={disabled}
              onClick={() => {
                onChange({ ...theme, backgroundImageUrl: null });
                if (backgroundInput.current) backgroundInput.current.value = '';
              }}
            >
              Remove background
            </button>
          ) : null}
        </div>
      </div>

      <p className="muted field-hint">PNG, JPEG or WebP, up to 5 MB.</p>

      <div className="field">
        <label htmlFor="theme-columns">Poll layout</label>
        <select
          id="theme-columns"
          disabled={disabled}
          value={theme.questionColumns ?? 1}
          onChange={(e) =>
            onChange({ ...theme, questionColumns: Number(e.target.value) as QuestionColumns })
          }
        >
          {COLUMN_CHOICES.map(({ value, label }) => (
            <option value={value} key={value}>
              {label}
            </option>
          ))}
        </select>
        <p className="muted field-hint">
          Wider displays fit more. A screen with fewer questions than this still fills the width.
        </p>
      </div>
      {uploading ? <p className="muted">Uploading {uploading}…</p> : null}
      {uploadError ? (
        <p className="form-error" role="alert">
          {uploadError}
        </p>
      ) : null}

      {themeContrastWarnings(theme).map((warning) => (
        <p className="form-error" role="alert" key={warning.message}>
          {warning.message}
        </p>
      ))}

      <ThemePreview theme={theme} />
    </div>
  );
}

function ThemePreview({ theme }: { theme: Theme }) {
  return (
    <div
      className="theme-preview"
      style={{
        background: theme.backgroundImageUrl
          ? `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url(${theme.backgroundImageUrl}) center/cover`
          : theme.backgroundColor,
        color: theme.textColor,
      }}
    >
      {theme.logoUrl ? <img src={theme.logoUrl} alt="" className="theme-preview-logo" /> : null}
      <p className="theme-preview-question">Which release should we ship first?</p>
      <div className="theme-preview-bar">
        <span style={{ background: theme.primaryColor, width: '64%' }} />
      </div>
      <div className="theme-preview-bar">
        <span style={{ background: theme.accentColor, width: '36%' }} />
      </div>
    </div>
  );
}
